import React, { useEffect, useRef, useState } from "react";
import { Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";

// Reusable service/custom-code search picker.
//
// Originally lived inline in CreateInvoice.jsx; lifted into its own module so
// the appointment modals (AddAppointment / EditAppointments) can drop the
// dropdown in favour of a search experience without dragging the
// custom-code toggle along.
//
// Props
//  - services            (required)  Array of service docs to search.
//  - customCodes         (optional)  Array of custom-code docs. Ignored when
//                                    hideCustomCodes is true.
//  - hideCustomCodes     (optional)  When true, hides the mode-toggle button
//                                    and never reaches the customCodes list.
//                                    Use this anywhere the caller wants only
//                                    services (e.g. appointment booking).
//  - onSelect(id)        (required)  Fired with the picked service's _id.
//  - selectedServiceName (optional)  When provided, renders a green chip
//                                    above the input showing the currently
//                                    selected service. Pair with onClear so
//                                    the user can reset and re-search.
//  - onClear             (optional)  Fired when the user clicks the X on the
//                                    selected-service chip.
//  - disabled            (optional)  Locks both the toggle and the search
//                                    field (used by EditAppointments when
//                                    the modal is in read-only mode).
//  - isInvalid           (optional)  Adds Bootstrap red-border styling to
//                                    the input (used for required-field
//                                    validation on appointment create).
export default function ServiceLinePicker({
  services,
  customCodes,
  onSelect,
  hideCustomCodes = false,
  selectedServiceName = "",
  onClear,
  disabled = false,
  isInvalid = false,
}) {
  const [mode, setMode] = useState("service");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function onDocMouseDown(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const isCustomCode = !hideCustomCodes && mode === "custom_code";
  // Defence in depth: even if the parent fetched mixed types, restrict each
  // list to its declared type so toggling actually changes the results.
  // Soft-deleted services may be present in the parent's list (it fetches
  // with includeDeleted so historical references on edit forms still resolve
  // by id), but they must NEVER appear in the search dropdown — a deleted
  // service should not be re-selectable on a new appointment / invoice line.
  const rawList = isCustomCode ? customCodes : services;
  const typedList = (rawList || [])
    .filter((s) => !s?.deleted)
    .filter((s) =>
      isCustomCode
        ? s?.serviceType === "custom_code"
        : s?.serviceType !== "custom_code"
    );
  const q = query.trim().toLowerCase();
  const filtered = typedList
    .filter((s) => !q || (s?.servicesName ?? "").toLowerCase().includes(q))
    .slice()
    .sort((a, b) =>
      (a?.servicesName ?? "")
        .toLowerCase()
        .localeCompare((b?.servicesName ?? "").toLowerCase())
    )
    .slice(0, 50);

  const handleToggle = () => {
    if (hideCustomCodes) return;
    setMode((m) => (m === "service" ? "custom_code" : "service"));
    setQuery("");
    setOpen(true);
  };

  const handlePick = (svc) => {
    onSelect(svc._id);
    setOpen(false);
    setQuery("");
  };

  // When a service is selected and the caller passed a clear handler, render
  // the selection inside a read-only text box that fills most of the modal
  // width, with a trash button alongside it. Clicking trash fires onClear,
  // which clears the parent's selectedServiceName and brings the search
  // input back into view on the next render. The read-only Form.Control
  // makes the value look like a populated form field rather than loose
  // text so it's visually obvious that a selection has been made.
  if (selectedServiceName && onClear) {
    return (
      <div className="d-flex align-items-center">
        <Form.Control
          type="text"
          readOnly
          value={selectedServiceName}
          disabled={disabled}
          style={{ width: "75%", backgroundColor: "#e9ecef", cursor: "default" }}
        />
        {!disabled && (
          <button
            type="button"
            aria-label="Remove selected service"
            title="Remove selected service"
            className="btn btn-link text-danger ms-2 p-0"
            onClick={onClear}
            style={{ textDecoration: "none", fontSize: "1.1rem" }}
          >
            <i className="fa fa-trash" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div className="d-flex align-items-center">
        {!hideCustomCodes && (
          <>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {isCustomCode ? "Search Services" : "Search Custom Codes"}
                </Tooltip>
              }
            >
              <Button
                variant="outline-primary"
                type="button"
                onClick={handleToggle}
                className="p-0 d-flex align-items-center justify-content-center"
                style={{ width: 38, height: 31 }}
                aria-label="Switch search mode"
                disabled={disabled}
              >
                <i
                  className={
                    isCustomCode
                      ? "fa-light fa-clipboard-list-check"
                      : "fa-light fa-tag"
                  }
                />
              </Button>
            </OverlayTrigger>

            <span
              className="mx-1 d-flex align-items-center"
              aria-hidden="true"
            >
              <i className="fa-light fa-arrow-left-arrow-right fs-6 text-primary" />
            </span>
          </>
        )}

        <Form.Control
          size="sm"
          type="text"
          placeholder={
            isCustomCode ? "Search custom codes…" : "Search services…"
          }
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="flex-grow-1"
          disabled={disabled}
          isInvalid={isInvalid}
        />
      </div>
      {open && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 2px)",
            left: 0,
            right: 0,
            zIndex: 1050,
            background: "#fff",
            border: "1px solid #ced4da",
            borderRadius: 4,
            maxHeight: 240,
            overflowY: "auto",
            boxShadow: "0 2px 8px rgba(0,0,0,.08)",
          }}
        >
          {filtered.length === 0 ? (
            <div className="text-muted small p-2">
              {isCustomCode
                ? "No custom codes match."
                : "No services match."}
            </div>
          ) : (
            filtered.map((s) => {
              // Stored flatRate already carries an "R " prefix (e.g.
              // "R 388.50"). Strip any leading R/space and re-add a single
              // "R " so the label reads "<code> - <invoice description>
              // R 388.50" regardless of how the source was saved.
              const flatRaw = s?.pricing?.flatRate;
              const flatNumeric =
                flatRaw == null || flatRaw === ""
                  ? null
                  : String(flatRaw).replace(/^R\s*/i, "").trim();
              const invDesc = (s?.pricing?.invoiceDescription || "").trim();
              return (
                <div
                  key={s._id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handlePick(s);
                  }}
                  className="px-2 py-1 service-picker-item"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f0f6ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <span className="fw-semibold">
                    {s.servicesName || "(unnamed)"}
                  </span>
                  {isCustomCode && invDesc && (
                    <span className="text-muted small ms-2">— {invDesc}</span>
                  )}
                  {isCustomCode && flatNumeric && (
                    <span className="text-muted small ms-2">
                      R {flatNumeric}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
